import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight, Sun, Moon } from "lucide-react";
import { useLang } from "../i18n.jsx";
import { motionOk } from "../lib/motionOk.js";
import { Magnetic } from "../lib/Interactive.jsx";

function LangSwitch() {
  const { lang, setLang } = useLang();
  const activeCls = "rounded-full bg-paper text-ink font-bold";
  const idleCls = "text-paper/70 hover:text-paper";
  return (
    <div className="flex items-center gap-0.5 rounded-full border border-paper/15 p-0.5" role="group" aria-label="Language">
      {["id", "en"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          aria-pressed={lang === l}
          className={`grid place-items-center w-8 h-7 rounded-full text-[11px] ${lang === l ? activeCls : idleCls}`}
        >
          {l === "id" ? "ID" : "EN"}
        </button>
      ))}
    </div>
  );
}

export default function NavBar() {
  const [open, setOpen] = useState(false);
  const { content, theme, toggleTheme, waLink } = useLang();
  const { studio, nav, navCta } = content;
  return (
    <motion.nav
      initial={{ y: -20, opacity: 0, x: "-50%" }}
      animate={{ y: 0, opacity: 1, x: "-50%" }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-4 left-1/2 z-40 w-[96%] max-w-6xl -translate-x-1/2"
    >
      <div className="flex items-center justify-between gap-3 rounded-full border border-paper/10 bg-ink2/80 backdrop-blur-xl px-4 py-2.5 shadow-2xl shadow-black/40">
        <a href="#top" data-cursor className="flex shrink-0 items-center gap-2.5">
          <span className="grid place-items-center w-8 h-8 rounded-full bg-acid text-ink font-display font-extrabold text-sm">
            {studio.short}
          </span>
          <span className="hidden sm:flex flex-col leading-none">
            <span className="whitespace-nowrap font-display font-bold text-[13px] text-paper">{studio.name}</span>
            <span className="hidden xl:block whitespace-nowrap text-[9px] uppercase tracking-[0.2em] text-paper/55">{studio.tag}</span>
          </span>
        </a>

        <ul className="hidden lg:flex items-center gap-1">
          {nav.map((n) => (
            <li key={n.href}>
              <a
                href={n.href}
                data-cursor
                className="px-2.5 py-2 rounded-full text-[13px] text-paper/70 hover:text-paper hover:bg-paper/5 transition whitespace-nowrap"
              >
                {n.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2">
          <button
            onClick={toggleTheme}
            aria-label={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
            className="grid place-items-center w-9 h-9 rounded-full border border-paper/15 text-paper/80 hover:text-paper transition"
          >
            {theme === "light" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
          </button>
          <LangSwitch />
        </div>

        <div className="hidden xl:block">
          <Magnetic>
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              data-cursor
              className="group flex items-center gap-2 rounded-full bg-acid px-5 py-2.5 text-[13px] font-bold text-ink hover:bg-paper transition-colors"
            >
              {navCta}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </a>
          </Magnetic>
        </div>

        <button
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          className="lg:hidden grid place-items-center w-9 h-9 rounded-full border border-paper/10 text-paper"
        >
          {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={motionOk ? { opacity: 0, y: -8 } : false}
            animate={motionOk ? { opacity: 1, y: 0 } : undefined}
            exit={motionOk ? { opacity: 0, y: -8 } : undefined}
            className="lg:hidden mt-2 rounded-2xl border border-paper/10 bg-ink2/95 backdrop-blur-xl p-3"
          >
            <ul className="flex flex-col gap-1">
              {nav.map((n) => (
                <li key={n.href}>
                  <a
                    href={n.href}
                    onClick={() => setOpen(false)}
                    className="block px-3 py-3 rounded-xl text-paper/80 hover:bg-paper/5 transition"
                  >
                    {n.label}
                  </a>
                </li>
              ))}
            </ul>
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="mt-2 flex items-center justify-center gap-2 rounded-full bg-acid px-4 py-3 text-sm font-bold text-ink"
            >
              {navCta} <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
